import { useState, useEffect, useRef } from 'react';

/**
 * Hook to highlight newly added rows in a list/table.
 * It tracks the data array and identifies newly added items,
 * returning a helper function to apply a CSS class.
 * 
 * @param {Array} data - The array of records (e.g., table rows)
 * @param {string} idKey - The unique identifier key in each record (default: '_id')
 * @param {number} duration - How long the highlight lasts in ms (default: 5000)
 * @param {string} highlightClass - The CSS class to apply for highlighting (default: 'bg-sky-100/80')
 * @param {Array} watchKeys - Array of keys to watch for changes (e.g., ['status'])
 */
export const useRowHighlight = (data = [], idKey = '_id', duration = 5000, highlightClass = 'bg-sky-100/80', watchKeys = []) => {
  const [highlightedIds, setHighlightedIds] = useState(new Set());
  const prevDataRef = useRef();

  useEffect(() => {
    // Check URL for highlight parameter on mount or when data changes
    const urlParams = new URLSearchParams(window.location.search);
    const highlightParam = urlParams.get('highlight');
    
    let initialHighlight = null;
    if (highlightParam && data.some(item => item[idKey] === highlightParam)) {
      initialHighlight = highlightParam;
      // Optionally scroll to it
      setTimeout(() => {
        const el = document.getElementById(`row-${highlightParam}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    // If it's the very first render, just store the data and don't highlight anything.
    if (!prevDataRef.current) {
      prevDataRef.current = new Map(data.map(item => [item[idKey], item]));
      if (initialHighlight) {
        setHighlightedIds(new Set([initialHighlight]));
        setTimeout(() => setHighlightedIds(new Set()), duration);
      }
      return;
    }

    const currentDataMap = new Map(data.map(item => [item[idKey], item]));
    const newHighlightedIds = new Set(initialHighlight ? [initialHighlight] : []);
    
    // Check for newly added items or updated watched keys
    data.forEach(item => {
      const id = item[idKey];
      const prevItem = prevDataRef.current.get(id);

      if (!prevItem) {
        // It's a new item
        newHighlightedIds.add(id);
      } else if (watchKeys.length > 0) {
        // It's an existing item, check if watched keys changed
        const hasChanged = watchKeys.some(key => prevItem[key] !== item[key]);
        if (hasChanged) {
          newHighlightedIds.add(id);
        }
      }
    });

    if (newHighlightedIds.size > 0) {
      setHighlightedIds(prev => new Set([...prev, ...newHighlightedIds]));
      
      const timer = setTimeout(() => {
        setHighlightedIds(prev => {
          const updated = new Set(prev);
          newHighlightedIds.forEach(id => updated.delete(id));
          return updated;
        });
      }, duration);
      
      prevDataRef.current = currentDataMap;
      return () => clearTimeout(timer);
    }
    
    prevDataRef.current = currentDataMap;
  }, [data, idKey, duration, watchKeys]);

  /**
   * Helper function to compute the row class name.
   * @param {Object} row - The row data object
   * @param {string} baseClass - Existing classes to append to
   */
  const getHighlightClass = (row, baseClass = '') => {
    const isHighlighted = row && highlightedIds.has(row[idKey]);
    
    // Apply a smooth transition so it fades nicely when the highlight is removed
    return isHighlighted 
      ? `${highlightClass} transition-colors duration-500 ease-in-out ${baseClass}`
      : `transition-colors duration-1000 ease-in-out ${baseClass}`;
  };

  return { highlightedIds, getHighlightClass };
};

export default useRowHighlight;
